/**
 * What every rank on every page says: its position, the size of the pool, and who the
 * pool is. One wording, so a rank on the Scorecard, Employees, the person page, the
 * month grid, My team's metrics and the Supervisors cards all read the same way.
 *
 * Shayna, 2026-10-02 (in-app review comments 9, 14, 17, 20):
 *  - Ranked against the people in your OWN job title — a supervisor against
 *    supervisors with the same title, not every front-line supervisor.
 *  - The comparison group is said on hover of the rank itself, not on the side.
 *  - Everyone in the title is in the pool, data or not. Someone with nothing to rank
 *    on sits at the bottom, labelled "no data" — not a real last place.
 *  - A person page says the rank in words: "Rank 5 of 28 HMO Billing Specialists".
 *
 * See MEASURE-RULES.md §10.7.
 */

/** What a rank says for somebody who had left before the period. One wording everywhere. */
export const NOT_RANKED_TERMINATED =
  'Terminated before this period — not ranked. People are ranked for the months they were active.'

/** The facts a rank is described from. */
export interface RankFacts {
  /** Dense rank, best first. Null when not ranked (no data, too few, or terminated). */
  rank: number | null
  /** Everyone in the pool, no-data included. The "of N". */
  of: number
  /** How many in the pool have no value to rank on. */
  noData: number
  /** Whether this one has a value. False → "no data", at the bottom. */
  hasValue: boolean
  /** The floor: fewer than this many WITH a value and nobody is ranked. */
  floor: number
  /** Who the pool is, plural: "HMO Billing Specialists". */
  group: string
  /** Had left before the period (or the month), so is not ranked. */
  terminated?: boolean
}

/** Last words that name a function, not a person — pluralised as "… staff". */
const NOT_A_PERSON = new Set(['billing', 'unknown', 'support', 'staff', 'management'])

function pluralWord(w: string): string {
  if (/[^aeiou]y$/i.test(w)) return `${w.slice(0, -1)}ies`
  if (/(s|x|z|ch|sh)$/i.test(w)) return `${w}es`
  return `${w}s`
}

/**
 * A job title as a group of people: "HMO Billing Specialist" → "HMO Billing
 * Specialists", "Director of Medicaid Billing" → "Directors of Medicaid Billing",
 * "Manager, Revenue Cycle Services" → "Managers, Revenue Cycle Services", "Medicare
 * Billing" → "Medicare Billing staff". Never guesses on a blank.
 */
export function titlePlural(title: string | null | undefined): string {
  const t = (title ?? '').trim()
  if (!t) return 'people with no job title'
  // The person-noun is the head: before " of " or ", " where there is one.
  const m = /^(.*?)( of |, )(.*)$/.exec(t)
  const head = m ? m[1]! : t
  const tail = m ? `${m[2]}${m[3]}` : ''
  const words = head.split(' ')
  const last = words[words.length - 1]!
  if (NOT_A_PERSON.has(last.toLowerCase())) return `${t} staff`
  words[words.length - 1] = pluralWord(last)
  return `${words.join(' ')}${tail}`
}

/** "(28 includes 3 with no data)", or nothing when everyone has data. */
const noDataNote = (f: RankFacts) =>
  f.noData > 0 ? ` (${f.of} includes ${f.noData} with no data)` : ''

/**
 * The rank, in words, for a hover title or a hero line.
 *
 *   Rank 5 of 28 HMO Billing Specialists in Jan–Aug 2026 (28 includes 3 with no data)
 *   No data in Jan–Aug 2026 — listed at the bottom of 28 HMO Billing Specialists, not ranked
 *   Not ranked: only 3 of 4 Medicare Supervisors have data in Jan–Aug 2026 — fewer than 5
 *
 * `by` names what was ranked where the hover does not already say it: "Rank 5 of 28
 * HMO Billing Specialists on score in …", "No score in …".
 */
export function rankText(f: RankFacts, period: string, by?: string): string {
  if (f.terminated) return NOT_RANKED_TERMINATED
  const compared = f.of - f.noData
  if (f.of <= 1) return `Not ranked: the only one with this job title in ${period}`
  // A pool under the floor is too small whatever its data; say so, not "0 of 2 have data".
  if (f.of < f.floor)
    return `Not ranked: only ${f.of} ${f.group} in ${period} — fewer than ${f.floor} to rank against`
  if (compared === 0) return `Not ranked: none of the ${f.of} ${f.group} have data in ${period}`
  if (compared < f.floor)
    return `Not ranked: only ${compared} of ${f.of} ${f.group} have data in ${period} — fewer than ${f.floor} to rank against`
  if (!f.hasValue || f.rank === null)
    return `No ${by ?? 'data'} in ${period} — listed at the bottom of ${f.of} ${f.group}, not ranked${noDataNote(f)}`
  return `Rank ${f.rank} of ${f.of} ${f.group}${by ? ` on ${by}` : ''} in ${period}${noDataNote(f)}`
}

/** What a rank cell prints: "5/28", "no data", "no cohort" or a dash for somebody who has left. */
export function rankShort(f: RankFacts): string {
  if (f.terminated) return '—'
  if (f.of - f.noData < f.floor) return 'no cohort'
  if (!f.hasValue || f.rank === null) return 'no data'
  return `${f.rank}/${f.of}`
}

/** The rank cell's state, for styling. */
export const rankState = (f: RankFacts): 'ranked' | 'noData' | 'noCohort' | 'terminated' =>
  f.terminated
    ? 'terminated'
    : f.of - f.noData < f.floor
      ? 'noCohort'
      : !f.hasValue || f.rank === null
        ? 'noData'
        : 'ranked'
